import CssBaseline from '@mui/material/CssBaseline'
import { i18n } from 'i18next'
import { createContext, useEffect, useState } from 'react'
import MainLayout from './components/Layout/MainLayout.tsx'
import './i18n.ts'
import { AlertProvider } from './providers/alert'
import { AuthProvider } from './providers/auth'
import { EnvConfigProvider } from './providers/envConfig'
import { ParentalConsentRouter } from './router'
import theme, { WayshrineThemeProvider } from './theme'
import { getCurrentLanguageFromUrl } from './utils/language.ts'

export const DwemerBootsI18nContext = createContext({} as i18n)

declare const bnetDwemerBoots: {
  app: {
    store: {
      subscribe: (callback: () => void) => void
      getState: () => unknown // we do not access state directly
      dispatch: (action: { type: string; data: unknown }) => void
    }
    localization: {
      i18n: i18n
    }
  }
}

export type BnetDispatch = ({ type, data }: { type: string; data: unknown }) => void
export type BnetStore = { dispatch: BnetDispatch }
export const BnetStoreContext = createContext<BnetStore | undefined>(undefined)

const App = () => {
  const [bnetStore, setBnetStore] = useState<BnetStore | undefined>(undefined)
  const [, setBnetState] = useState(null as unknown) // we do not access state directly
  const [dwemerBootsI18n, setDwemerBootsI18n] = useState({} as i18n)

  // This useEffect waits for the bnetDwemerBoots object to be defined before subscribing to the store
  useEffect(() => {
    let timerId = 0
    const waitForBnet = () => {
      if (
        typeof bnetDwemerBoots === 'undefined' ||
        typeof bnetDwemerBoots.app.store === 'undefined'
      ) {
        clearTimeout(timerId)
        timerId = Number(setTimeout(waitForBnet, 100))
      } else {
        bnetDwemerBoots.app.store.subscribe(() => {
          setBnetState(bnetDwemerBoots.app.store.getState())
        })
        setDwemerBootsI18n(bnetDwemerBoots.app.localization.i18n)
        setBnetStore(bnetDwemerBoots.app.store)
      }
    }
    waitForBnet()
    return () => clearTimeout(timerId)
  })

  useEffect(() => {
    if (!dwemerBootsI18n || !bnetDwemerBoots || !dwemerBootsI18n.changeLanguage) return
    const currentLanguageFromUrl = getCurrentLanguageFromUrl()
    if (currentLanguageFromUrl != dwemerBootsI18n.language) {
      bnetDwemerBoots.app.store.dispatch({
        type: 'SET_LANGUAGE',
        data: currentLanguageFromUrl,
      })
      dwemerBootsI18n.changeLanguage(currentLanguageFromUrl).catch((error) => {
        console.error('failed to change language:', error)
      })
    }
  }, [bnetDwemerBoots, dwemerBootsI18n.language])

  return (
    <WayshrineThemeProvider theme={theme}>
      <AlertProvider>
        <DwemerBootsI18nContext.Provider value={dwemerBootsI18n}>
          <BnetStoreContext.Provider value={bnetStore}>
            <EnvConfigProvider>
              <AuthProvider>
                <MainLayout>
                  <CssBaseline />
                  <ParentalConsentRouter dwemerBootsI18n={dwemerBootsI18n} />
                </MainLayout>
              </AuthProvider>
            </EnvConfigProvider>
          </BnetStoreContext.Provider>
        </DwemerBootsI18nContext.Provider>
      </AlertProvider>
    </WayshrineThemeProvider>
  )
}

export default App
